import { Code, ConnectError } from '@connectrpc/connect'
import type { TFunction } from 'i18next'

import { intradayBlockedReasonMessage } from '~/models/intradayServerError'

/**
 * 入札追加 (CreateBiddingIntradayOrder) の失敗を 受付前 / GC 超過 / その他 の
 * 固定文言に訳す (spec intraday-bidding.md AC-057)。 内部処理のシステムメッセージ
 * (ConnectError の `message`) を画面にそのまま連結しない (= spot 側
 * `translateSpotWriteError` と同じ「エラーコード → 固定文言」方式に統一)。
 *
 * 受付前 (AC-BIN-089c) と GC 超過はどちらも FailedPrecondition で届くため、
 * まず `google.rpc.ErrorInfo` detail の reason で引き分ける。 フォーミュラの ÷ 0 は
 * InvalidArgument で届くので、 reason は code によらず先に見る。 reason が無い
 * FailedPrecondition は従来どおり GC 超過相当として扱う (backend は GC ガードに
 * まだ reason を載せていないため)。
 *
 * 既知の理由 (受付前 / GC 超過 / フォーミュラ・手数料の理由) なら固定文言、 分からなければ
 * undefined を返す。 一括作成のバナーが、 理由の分からない失敗を文言の比較に頼らず見分ける。
 */
export function intradayBidCreateErrorReason(
  err: unknown,
  t: TFunction,
): string | undefined {
  if (err instanceof ConnectError) {
    const reasonMessage = intradayBlockedReasonMessage(err, t)
    if (reasonMessage !== undefined) return reasonMessage
    if (err.code === Code.FailedPrecondition) {
      return t('page:biddingIntraday.bidCreate.gcExceeded')
    }
  }
  return undefined
}

/**
 * サーバーが reason (ErrorInfo) で明示した理由だけを固定文言にする。 reason の無い
 * FailedPrecondition を GC 超過に丸める [[intradayBidCreateErrorReason]] と違い、 丸めた
 * ものは undefined のまま返す。 一括作成の全件失敗トーストが、 実際の原因 (上限数量超過・
 * 完了済ケース等) と食い違う GC 超過の文言を出さないために使う。
 */
export function intradayBidCreateExplicitReason(
  err: unknown,
  t: TFunction,
): string | undefined {
  return err instanceof ConnectError
    ? intradayBlockedReasonMessage(err, t)
    : undefined
}

/** [[intradayBidCreateErrorReason]] で分からない失敗は汎用の失敗文言にする。 */
export function translateIntradayBidCreateError(
  err: unknown,
  t: TFunction,
): string {
  return (
    intradayBidCreateErrorReason(err, t) ??
    t('page:biddingIntraday.bidCreate.error')
  )
}
